// Guardrail: Veera outputs must remain descriptive and coach-facing.
// Do NOT add scoring, evaluation, or behavior direction here.
// Coach-first rule: Never surface these outputs to leaders without explicit coach action.
import { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { supabase, SUPABASE_URL } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { useAuth } from "@/hooks/useAuth";
import { sanitizeCoachMessageContent } from "@/lib/coachChatMessageContent";
import { toast } from "sonner";
import { readSSEStream } from './useSSEStream';
import { getVeeraContextualOpenerSeed } from "@/lib/veeraContextualOpenerSeed";
import type { HarnessPayload } from "@/types/harnessPayload";
import { extractSupabaseEdgeFunctionErrorCodeAsync } from "@/utils/edgeFunctionErrors";

interface MetaEventPayload {
  sessionId?: string;
  messageId?: string;
  coachInsight?: CoachInsight | null;
  contentOverride?: string;
  conversationState?: CoachConversationState | null;
  harnessPayload?: HarnessPayload | null;
  evidenceRefs?: CoachEvidenceRef[];
  selectedLeaderContext?: SelectedLeaderContextAttestation | null;
}

interface CoachEvidenceRef {
  id: string;
  label: string;
  route: string | null;
}

type SelectedLeaderEvidenceStatus = "ready" | "empty" | "degraded" | "unavailable";

interface SelectedLeaderContextAttestation {
  leaderId: string;
  leaderName: string;
  evidenceStatus: SelectedLeaderEvidenceStatus;
  evidenceAsOf: string;
}

type CoachChatSSEEvent =
  | { event: 'delta'; data: { text: string } }
  | { event: 'meta'; data: MetaEventPayload }
  | { event: 'done'; data: Record<string, never> }
  | { event: 'error'; data: { error: string } }
  | { event: string; data: unknown };

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  createdAt: Date;
  coachInsight?: CoachInsight | null;
  feedbackSourceId?: string;
  evidenceRefs?: CoachEvidenceRef[];
  selectedLeaderContext?: SelectedLeaderContextAttestation | null;
}

type CoachChatMessageRow = Pick<
  Database["public"]["Tables"]["coach_chat_messages"]["Row"],
  "id" | "role" | "content" | "created_at"
>;

interface RecoveredSessionMessages {
  sessionId: string;
  messages: ChatMessage[];
}

export interface CoachConversationState {
  sessionId: string;
  surfaceRole: "coach";
  currentGoal?: string | null;
  currentHypothesis?: string | null;
  recommendedNextMove?: string | null;
  unresolvedQuestion?: string | null;
  confidence?: "low" | "medium" | "high";
  thinEvidence?: boolean;
  contradictionCount?: number;
  freshness?: "fresh" | "recent" | "stale" | "unknown" | null;
  stateVersion?: number;
}

export interface CoachInsight {
  mode: "global" | "leader" | "coach";
  confidence: "high" | "medium" | "low";
  situation: string;
  evidence: Array<{ text: string; href?: string }>;
  interpretation: string;
  coachMove: string;
  questions: string[];
  watchout: string;
  missingData: string[];
}

interface PageContext {
  pageName: string;
  leaderId?: string;
  relationshipRevision?: string;
  relationshipCreatedAt?: string;
  data?: Record<string, unknown>;
}

interface CoachContextualEntry {
  threadTitle?: string | null;
  assistantOpener?: {
    carryOverSummary?: string | null;
    whyItMattersNow?: string | null;
    question?: string | null;
    shouldAskQuestion?: boolean;
  } | null;
}

interface CoachChatTurn {
  role: "user" | "assistant";
  content: string;
}

interface UseCoachChatOptions {
  transcriptUploadId?: string | null;
  coachCallId?: string | null;
}

interface SendMessageOptions {
  suppressUserEcho?: boolean;
}

export type CoachCloseAction = "journal" | "next_session" | "leader_prep" | "all";
export type CoachChatContextType = "global" | "leader" | "coach";
export type CoachHistoryScope = "recent" | "rolling_30d" | "rolling_60d" | "rolling_90d" | "rolling_365d" | "full";

export const COACH_CHAT_STREAM_TIMEOUT_MS = 30_000;

const GLOBAL_SESSION_STORAGE_KEY = "veera-coach-chat-session-global";
const GLOBAL_MESSAGES_STORAGE_KEY = "veera-coach-chat-messages-global";
const CLOSE_CAPTURE_SUMMARY_MAX_LENGTH = 9000;

const getSessionStorageKey = (
  contextType: CoachChatContextType,
  leaderId?: string,
  relationshipRevision?: string,
  conversationId?: string,
  userId?: string | null,
) => {
  const userScope = userId === undefined ? "" : userId ? `-user-${encodeURIComponent(userId)}` : "-anonymous";
  if (conversationId) {
    const contextScope = contextType === "leader"
      ? `leader-${encodeURIComponent(leaderId || "missing")}-relationship-${encodeURIComponent(relationshipRevision || "missing")}`
      : contextType;
    return `veera-coach-chat-session-${conversationId}-${contextScope}${userScope}`;
  }
  if (contextType === "leader" && leaderId) {
    return `veera-coach-chat-session-${leaderId}-relationship-${encodeURIComponent(relationshipRevision || "missing")}${userScope}`;
  }
  if (contextType === "coach") {
    return `veera-coach-chat-session-coach${userScope}`;
  }
  return `${GLOBAL_SESSION_STORAGE_KEY}${userScope}`;
};

const getMessagesStorageKey = (
  contextType: CoachChatContextType,
  leaderId?: string,
  relationshipRevision?: string,
  conversationId?: string,
  userId?: string | null,
) => {
  const userScope = userId === undefined ? "" : userId ? `-user-${encodeURIComponent(userId)}` : "-anonymous";
  if (conversationId) {
    const contextScope = contextType === "leader"
      ? `leader-${encodeURIComponent(leaderId || "missing")}-relationship-${encodeURIComponent(relationshipRevision || "missing")}`
      : contextType;
    return `veera-coach-chat-messages-${conversationId}-${contextScope}${userScope}`;
  }
  if (contextType === "leader" && leaderId) {
    return `veera-coach-chat-messages-${leaderId}-relationship-${encodeURIComponent(relationshipRevision || "missing")}${userScope}`;
  }
  if (contextType === "coach") {
    return `veera-coach-chat-messages-coach${userScope}`;
  }
  return `${GLOBAL_MESSAGES_STORAGE_KEY}${userScope}`;
};

const getLegacyConversationStorageKey = (
  kind: "session" | "messages",
  conversationId: string,
  userId?: string | null,
) => {
  const userScope = userId === undefined ? "" : userId ? `-user-${encodeURIComponent(userId)}` : "-anonymous";
  return `veera-coach-chat-${kind}-${conversationId}${userScope}`;
};

const getLegacyScopedStorageKey = (
  kind: "session" | "messages",
  contextType: CoachChatContextType,
  leaderId?: string,
  conversationId?: string,
  userId?: string | null,
) => {
  const userScope = userId === undefined ? "" : userId ? `-user-${encodeURIComponent(userId)}` : "-anonymous";
  if (conversationId) {
    const contextScope = contextType === "leader"
      ? `leader-${encodeURIComponent(leaderId || "missing")}`
      : contextType;
    return `veera-coach-chat-${kind}-${conversationId}-${contextScope}${userScope}`;
  }
  if (contextType === "leader" && leaderId) {
    return `veera-coach-chat-${kind}-${leaderId}${userScope}`;
  }
  if (contextType === "coach") {
    return `veera-coach-chat-${kind}-coach${userScope}`;
  }
  return `veera-coach-chat-${kind}-global${userScope}`;
};

const hydrateMessages = (raw: string | null): ChatMessage[] => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as Array<Omit<ChatMessage, "createdAt"> & { createdAt: string }>;
    return parsed.map((message) => ({
      ...message,
      content: sanitizeCoachMessageContent(message.content),
      createdAt: new Date(message.createdAt),
    }));
  } catch {
    return [];
  }
};

const truncateCloseCaptureSummary = (summary: string): string => (
  summary.length > CLOSE_CAPTURE_SUMMARY_MAX_LENGTH
    ? summary.slice(0, CLOSE_CAPTURE_SUMMARY_MAX_LENGTH)
    : summary
);

const readEphemeralChatStorage = (key: string): string | null => {
  try {
    return window.sessionStorage.getItem(key) ?? window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeEphemeralChatStorage = (key: string, value: string) => {
  try {
    window.sessionStorage.setItem(key, value);
    window.localStorage.removeItem(key);
  } catch {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // Ignore storage cleanup failures in privacy-restricted browsers.
    }
  }
};

const removeEphemeralChatStorage = (key: string) => {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // Ignore storage cleanup failures in privacy-restricted browsers.
  }
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Ignore storage cleanup failures in privacy-restricted browsers.
  }
};

function mapPersistedCoachMessages(rows: CoachChatMessageRow[]): ChatMessage[] {
  return rows.map((message) => ({
    id: message.id,
    role: message.role as "user" | "assistant",
    content: sanitizeCoachMessageContent(message.content),
    createdAt: new Date(message.created_at),
    feedbackSourceId: message.role === "assistant" ? message.id : undefined,
  }));
}

export function useCoachChat(
  pageContext?: PageContext,
  contextType: CoachChatContextType = "global",
  historyScope: CoachHistoryScope = "recent",
  conversationId?: string,
  options?: UseCoachChatOptions,
) {
  const { user } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [conversationState, setConversationState] = useState<CoachConversationState | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [streamingContent, setStreamingContent] = useState<string>('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [harnessPayload, setHarnessPayload] = useState<HarnessPayload | null>(null);
  const streamingContentRef = useRef('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const activeCloseRequestIdRef = useRef<string | null>(null);
  const seededContextualConversationRef = useRef<string | null>(null);
  const [hasResolvedInitialMessages, setHasResolvedInitialMessages] = useState(false);
  // Per-request UUID — allows the event loop to detect stale responses when
  // a user navigates away and back without sending a new message (AbortController
  // handles in-flight cancellation; this guards the navigate-without-abort case).
  const activeRequestIdRef = useRef<string | null>(null);
  const requestContextBoundaryRef = useRef<string | null>(null);
  const authAccessTokenRef = useRef<{
    accessToken: string;
    userId: string;
  } | null>(null);
  const supabaseUrl = SUPABASE_URL;

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    return () => {
      activeRequestIdRef.current = null;
      activeCloseRequestIdRef.current = null;
      abortControllerRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    authAccessTokenRef.current = null;
    if (!user?.id) {
      return;
    }

    void supabase.auth.getSession().then(({ data }) => {
      if (!cancelled) {
        const session = data.session;
        authAccessTokenRef.current = session?.access_token && session.user.id === user.id
          ? { accessToken: session.access_token, userId: session.user.id }
          : null;
      }
    });

    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "TOKEN_REFRESHED" || event === "SIGNED_IN") {
        authAccessTokenRef.current = session?.access_token && session.user.id
          ? { accessToken: session.access_token, userId: session.user.id }
          : null;
        return;
      }

      if (event === "SIGNED_OUT") {
        authAccessTokenRef.current = null;
      }
    });

    return () => {
      cancelled = true;
      authListener.subscription.unsubscribe();
    };
  }, [user?.id]);

  const storageLeaderId = contextType === "leader" ? pageContext?.leaderId : undefined;
  const storageRelationshipRevision = contextType === "leader" ? pageContext?.relationshipRevision : undefined;
  const storageRelationshipCreatedAt = contextType === "leader" ? pageContext?.relationshipCreatedAt : undefined;
  const sessionStorageKey = getSessionStorageKey(contextType, storageLeaderId, storageRelationshipRevision, conversationId, user?.id ?? null);
  const messagesStorageKey = getMessagesStorageKey(contextType, storageLeaderId, storageRelationshipRevision, conversationId, user?.id ?? null);
  const [hydratedStorageKey, setHydratedStorageKey] = useState<string | null>(null);
  const currentStorageScopeKey = [
    user?.id || "anonymous",
    contextType,
    contextType === "leader" ? storageLeaderId || "missing-leader" : "no-leader",
    contextType === "leader" ? storageRelationshipRevision || "missing-relationship" : "no-relationship",
    conversationId || "no-conversation",
    sessionStorageKey,
    messagesStorageKey,
  ].join("|");
  const activeStorageScopeKeyRef = useRef(currentStorageScopeKey);
  activeStorageScopeKeyRef.current = currentStorageScopeKey;
  const hasCompleteScope = contextType !== "leader" || Boolean(storageLeaderId && storageRelationshipRevision);
  const isScopeReady = hasCompleteScope
    && hydratedStorageKey === currentStorageScopeKey
    && hasResolvedInitialMessages;
  const contextualEntry = (pageContext?.data as { contextualEntry?: CoachContextualEntry | null } | undefined)?.contextualEntry ?? null;
  const requestContextBoundary = JSON.stringify({
    leaderId: pageContext?.leaderId ?? null,
    selectedLeaderIds: pageContext?.data?.selectedLeaderIds ?? null,
    accessGeneration: pageContext?.data?.accessGeneration ?? null,
    relationshipRevision: pageContext?.relationshipRevision ?? null,
  });

  useEffect(() => {
    if (requestContextBoundaryRef.current === null) {
      requestContextBoundaryRef.current = requestContextBoundary;
      return;
    }
    if (requestContextBoundaryRef.current === requestContextBoundary) return;
    requestContextBoundaryRef.current = requestContextBoundary;
    abortControllerRef.current?.abort();
    activeRequestIdRef.current = null;
    streamingContentRef.current = "";
    setStreamingContent("");
    setIsLoading(false);
    setIsStreaming(false);
  }, [requestContextBoundary]);

  useLayoutEffect(() => {
    if (typeof window === "undefined") return;
    // Legacy keys were not user-scoped. Never hydrate them because a later
    // account in the same browser could otherwise see another coach's draft.
    removeEphemeralChatStorage(getLegacyScopedStorageKey("session", contextType, storageLeaderId, conversationId));
    removeEphemeralChatStorage(getLegacyScopedStorageKey("messages", contextType, storageLeaderId, conversationId));
    if (contextType === "leader") {
      // Leader cache keys predating relationship revisions were user-scoped,
      // but still unsafe after unlink/relink of the same leader ID.
      removeEphemeralChatStorage(getLegacyScopedStorageKey("session", contextType, storageLeaderId, conversationId, user?.id ?? null));
      removeEphemeralChatStorage(getLegacyScopedStorageKey("messages", contextType, storageLeaderId, conversationId, user?.id ?? null));
    }
    if (conversationId) {
      // Conversation storage predating exact scope keys could contain messages
      // from a different coach/global/leader context. Delete it instead of
      // attempting to infer which scope owned the cached content.
      removeEphemeralChatStorage(getLegacyConversationStorageKey("session", conversationId));
      removeEphemeralChatStorage(getLegacyConversationStorageKey("messages", conversationId));
      removeEphemeralChatStorage(getLegacyConversationStorageKey("session", conversationId, user?.id ?? null));
      removeEphemeralChatStorage(getLegacyConversationStorageKey("messages", conversationId, user?.id ?? null));
    }
    // Invalidate any in-flight request so its SSE events are dropped after a
    // thread/context switch. Without this, a response in flight for thread A
    // would pass the activeRequestIdRef guard and be appended to thread B.
    abortControllerRef.current?.abort();
    activeRequestIdRef.current = null;
    activeCloseRequestIdRef.current = null;
    setSessionId(readEphemeralChatStorage(sessionStorageKey) || conversationId || null);
    setConversationState(null);
    setHarnessPayload(null);
    setMessages(hydrateMessages(readEphemeralChatStorage(messagesStorageKey)));
    setIsLoading(false);
    setIsStreaming(false);
    setStreamingContent("");
    streamingContentRef.current = "";
    setHasResolvedInitialMessages(!conversationId);
    setError(null);
    setHydratedStorageKey(currentStorageScopeKey);
  }, [
    contextType,
    conversationId,
    currentStorageScopeKey,
    messagesStorageKey,
    sessionStorageKey,
    storageLeaderId,
    user?.id,
  ]);

  useEffect(() => {
    if (!user?.id || !conversationId || !hasCompleteScope) return;
    let cancelled = false;

    const loadPersistedMessages = async () => {
      type MessageRow = Pick<Database["public"]["Tables"]["coach_chat_messages"]["Row"], "id" | "role" | "content" | "created_at" | "context_type" | "leader_id">;
      const baseQuery = supabase
        .from("coach_chat_messages")
        .select("id, role, content, created_at, context_type, leader_id")
        .eq("coach_id", user.id)
        .eq("session_id", conversationId)
        .eq("context_type", contextType);
      const leaderScopedQuery = contextType === "leader"
        ? baseQuery.eq("leader_id", storageLeaderId as string)
        : null;
      const scopedQuery = leaderScopedQuery
        ? storageRelationshipCreatedAt
          ? leaderScopedQuery.gte("created_at", storageRelationshipCreatedAt)
          : leaderScopedQuery
        : baseQuery.is("leader_id", null);
      const { data, error }: { data: MessageRow[] | null; error: { message?: string } | null } = await scopedQuery
        .order("created_at", { ascending: true });

      if (cancelled || error || !data || data.length === 0) {
        if (!cancelled) {
          setHasResolvedInitialMessages(true);
        }
        return;
      }

      const hydrated = data.map((message) => ({
        id: message.id,
        role: message.role as "user" | "assistant",
        content: sanitizeCoachMessageContent(message.content),
        createdAt: new Date(message.created_at),
        feedbackSourceId: message.role === "assistant" ? message.id : undefined,
      }));

      setMessages(hydrated);
      setSessionId(conversationId);
      setHasResolvedInitialMessages(true);
    };

    void loadPersistedMessages();

    return () => {
      cancelled = true;
    };
  }, [contextType, conversationId, hasCompleteScope, storageLeaderId, storageRelationshipCreatedAt, user?.id]);

  const recoverPersistedSessionMessages = useCallback(async (
    persistedSessionId: string,
    knownAssistantIds: Set<string>,
  ): Promise<RecoveredSessionMessages | null> => {
    if (!user?.id || !persistedSessionId) return null;

    if (!hasCompleteScope) return null;
    const baseQuery = supabase
      .from("coach_chat_messages")
      .select("id, role, content, created_at, context_type, leader_id")
      .eq("coach_id", user.id)
      .eq("session_id", persistedSessionId)
      .eq("context_type", contextType);
    const leaderScopedQuery = contextType === "leader"
      ? baseQuery.eq("leader_id", storageLeaderId as string)
      : null;
    const scopedQuery = leaderScopedQuery
      ? storageRelationshipCreatedAt
        ? leaderScopedQuery.gte("created_at", storageRelationshipCreatedAt)
        : leaderScopedQuery
      : baseQuery.is("leader_id", null);
    const { data, error }: { data: CoachChatMessageRow[] | null; error: { message?: string } | null } = await scopedQuery
      .order("created_at", { ascending: true });

    if (error || !data || data.length === 0) return null;

    const hydrated = mapPersistedCoachMessages(data);
    const hasNewAssistant = hydrated.some((message) =>
      message.role === "assistant" && !knownAssistantIds.has(message.id),
    );
    if (!hasNewAssistant) return null;

    return {
      sessionId: persistedSessionId,
      messages: hydrated,
    };
  }, [contextType, hasCompleteScope, storageLeaderId, storageRelationshipCreatedAt, user?.id]);

  useEffect(() => {
    const contextualSeed = getVeeraContextualOpenerSeed({
      hasResolvedInitialMessages,
      messagesLength: messages.length,
      conversationId,
      sessionId,
      seededConversationId: seededContextualConversationRef.current,
      contextualEntry,
    });
    if (!contextualSeed) return;

    seededContextualConversationRef.current = contextualSeed.conversationSeedKey;
    setMessages([
      {
        id: crypto.randomUUID(),
        role: "assistant",
        content: contextualSeed.assistantContent,
        createdAt: new Date(),
      },
    ]);

    if (!sessionId && conversationId) {
      setSessionId(conversationId);
    }
  }, [contextualEntry, conversationId, hasResolvedInitialMessages, messages.length, sessionId]);

  useEffect(() => {
    if (!sessionId || typeof window === "undefined") return;
    if (hydratedStorageKey !== currentStorageScopeKey) return;
    writeEphemeralChatStorage(sessionStorageKey, sessionId);
  }, [currentStorageScopeKey, hydratedStorageKey, sessionStorageKey, sessionId]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (hydratedStorageKey !== currentStorageScopeKey) return;
    writeEphemeralChatStorage(
      messagesStorageKey,
      JSON.stringify(
        messages.map((message) => ({
          ...message,
          createdAt: message.createdAt.toISOString(),
        })),
      ),
    );
  }, [currentStorageScopeKey, hydratedStorageKey, messagesStorageKey, messages]);

  const sendMessage = useCallback(async (content: string, sendOptions?: SendMessageOptions) => {
    if (!user || !content.trim() || isLoading || !isScopeReady) return;
    const requestId = crypto.randomUUID();
    activeRequestIdRef.current = requestId;
    let receivedMeta = false;
    const suppressUserEcho = sendOptions?.suppressUserEcho === true;
    const persistedSessionId = sessionId || conversationId || null;
    const knownAssistantIds = new Set(
      messages.filter((message) => message.role === "assistant").map((message) => message.id),
    );
    const recentMessages: CoachChatTurn[] = messages
      .slice(-8)
      .map((message) => ({
        role: message.role,
        content: message.content,
      }))
      .filter((message) => message.content.trim().length > 0);

    setError(null);
    setIsLoading(true);

    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: "user",
      content: content.trim(),
      createdAt: new Date(),
    };
    if (!suppressUserEcho) {
      setMessages((prev) => [...prev, userMessage]);
    }

    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (activeRequestIdRef.current !== requestId) return;
      if (sessionError || !session) {
        throw new Error("Session expired. Please refresh the page.");
      }
      if (session.user.id !== user.id) {
        throw new Error("Session changed. Please try again.");
      }
      authAccessTokenRef.current = {
        accessToken: session.access_token,
        userId: session.user.id,
      };

      setIsLoading(true);
      setStreamingContent('');
      streamingContentRef.current = '';

      const body = {
        message: content.trim(),
        sessionId,
        contextType,
        historyScope,
        recentMessages,
        leaderId: contextType === "leader" ? pageContext?.leaderId : null,
        relationshipRevision: contextType === "leader" ? pageContext?.relationshipRevision : null,
        transcriptUploadId: options?.transcriptUploadId || null,
        coachCallId: options?.coachCallId || null,
        pageContext: pageContext ? {
          pageName: pageContext.pageName,
          leaderId: contextType === "leader" ? pageContext.leaderId : undefined,
          relationshipRevision: contextType === "leader" ? pageContext.relationshipRevision : undefined,
          data: pageContext.data,
        } : undefined,
      };

      // Abort any in-flight stream
      abortControllerRef.current?.abort();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      let streamTimedOut = false;
      let streamTimeoutId: number | null = null;
      const resetStreamTimeout = () => {
        if (streamTimeoutId !== null) {
          window.clearTimeout(streamTimeoutId);
        }
        streamTimeoutId = window.setTimeout(() => {
          streamTimedOut = true;
          abortController.abort();
        }, COACH_CHAT_STREAM_TIMEOUT_MS);
      };
      resetStreamTimeout();

      try {
        for await (const evt of readSSEStream<CoachChatSSEEvent>(
          'coach-chat',
          { ...body, stream: true },
          session,
          supabaseUrl,
          abortController.signal,
        )) {
          if (activeRequestIdRef.current !== requestId) return;
          resetStreamTimeout();

          if (evt.event === 'delta') {
            const text = (evt.data as Record<string, unknown>)?.text;
            if (typeof text !== 'string') continue;
            setIsLoading(false);
            setIsStreaming(true);
            streamingContentRef.current += text;
            setStreamingContent(streamingContentRef.current);
          } else if (evt.event === 'meta') {
            receivedMeta = true;
            const meta = evt.data as MetaEventPayload;
            setIsLoading(false);
            setIsStreaming(false);
            setStreamingContent('');
            if (meta.sessionId) setSessionId(meta.sessionId);
            if (meta.conversationState !== undefined) {
              setConversationState(meta.conversationState ?? null);
            }
            setHarnessPayload(meta.harnessPayload ?? null);
            const rawContent = meta.contentOverride ?? streamingContentRef.current;
            const hasPersistedAssistantId = typeof meta.messageId === "string" && meta.messageId.trim().length > 0;
            const assistantMessageId = hasPersistedAssistantId ? meta.messageId as string : crypto.randomUUID();
            const assistantMessage: ChatMessage = {
              id: assistantMessageId,
              role: 'assistant',
              content: sanitizeCoachMessageContent(rawContent),
              createdAt: new Date(),
              coachInsight: meta.coachInsight ?? null,
              feedbackSourceId: hasPersistedAssistantId ? meta.messageId : undefined,
              evidenceRefs: hasPersistedAssistantId ? meta.evidenceRefs ?? [] : [],
              selectedLeaderContext: hasPersistedAssistantId ? meta.selectedLeaderContext ?? null : null,
            };
            setMessages((prev) => [...prev, assistantMessage]);
            streamingContentRef.current = '';
          } else if (evt.event === 'error') {
            const errText = (evt.data as Record<string, unknown>)?.error;
            throw new Error(typeof errText === 'string' ? errText : 'Unknown stream error');
          }
        }
      } catch (streamError) {
        if (streamTimedOut) {
          throw new Error("Coach Veera took too long to respond. Please try again.");
        }
        throw streamError;
      } finally {
        if (streamTimeoutId !== null) {
          window.clearTimeout(streamTimeoutId);
        }
      }

      if (!receivedMeta && persistedSessionId) {
        const recovered = await recoverPersistedSessionMessages(persistedSessionId, knownAssistantIds);
        if (activeRequestIdRef.current !== requestId) return;
        if (recovered) {
          setMessages(recovered.messages);
          setSessionId(recovered.sessionId);
          setError(null);
        }
      }
    } catch (err: unknown) {
      // Ignore abort errors — these are intentional cancellations
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }

      if (activeRequestIdRef.current !== requestId) return;

      const recovered = persistedSessionId
        ? await recoverPersistedSessionMessages(persistedSessionId, knownAssistantIds)
        : null;
      if (activeRequestIdRef.current !== requestId) return;
      if (recovered) {
        setMessages(recovered.messages);
        setSessionId(recovered.sessionId);
        setError(null);
        return;
      }

      // Capture whether a partial response had been received before clearing it
      const hadPartialContent = streamingContentRef.current.length > 0;
      streamingContentRef.current = '';
      setStreamingContent('');

      console.error("Coach chat error:", err);
      const message = err instanceof Error ? err.message : "Failed to send message";
      setError(message);

      if (message.includes("Rate limit")) {
        toast.error("Too many messages. Please wait a moment and try again.");
      } else if (message.includes("credits")) {
        toast.error("AI service unavailable. Please try again later.");
      } else if (hadPartialContent) {
        toast.error("Response was interrupted. Please try again.");
      } else {
        toast.error("Couldn't reach Veera. Please try again.");
      }

      if (!suppressUserEcho) {
        setMessages((prev) => prev.filter((m) => m.id !== userMessage.id));
      }
    } finally {
      if (activeRequestIdRef.current === requestId) {
        activeRequestIdRef.current = null;
        abortControllerRef.current = null;
        setIsLoading(false);
        setIsStreaming(false);
      }
    }
  }, [
    user,
    isLoading,
    messages,
    sessionId,
    conversationId,
    pageContext,
    contextType,
    historyScope,
    supabaseUrl,
    recoverPersistedSessionMessages,
    options?.transcriptUploadId,
    options?.coachCallId,
    isScopeReady,
  ]);

  const closeConversation = useCallback(async ({
    actions,
    summary,
    leaderId,
    transcriptUploadId,
    coachCallId,
    silent = false,
  }: {
    actions: CoachCloseAction[];
    summary?: string;
    leaderId?: string | null;
    transcriptUploadId?: string | null;
    coachCallId?: string | null;
    /**
     * Skip appending the confirmation message to the visible thread. Required
     * for navigation-time captures: appending during a thread switch lands the
     * message in the target thread's state and corrupts its title/preview.
     */
    silent?: boolean;
  }) => {
    if (!user || isLoading || !isScopeReady) return null;
    const closeRequestId = crypto.randomUUID();
    const closeScopeKey = currentStorageScopeKey;
    activeCloseRequestIdRef.current = closeRequestId;
    setIsLoading(true);
    setError(null);

    try {
      const fallbackSummary = truncateCloseCaptureSummary(summary
        || [...messages].reverse().find((m) => m.role === "assistant")?.content
        || "Coach conversation captured.");

      const normalizedActions = actions.includes("all")
        ? ["journal", "next_session", "leader_prep", "all"] as CoachCloseAction[]
        : actions;

      const {
        data: { session: authenticatedSession },
        error: sessionError,
      } = await supabase.auth.getSession();
      if (
        activeCloseRequestIdRef.current !== closeRequestId ||
        activeStorageScopeKeyRef.current !== closeScopeKey
      ) return null;
      if (
        sessionError ||
        !authenticatedSession ||
        authenticatedSession.user.id !== user.id
      ) {
        throw new Error("Session changed. Please try again.");
      }

      const { data, error: fnError } = await supabase.functions.invoke("coach-chat", {
        headers: {
          Authorization: `Bearer ${authenticatedSession.access_token}`,
        },
        body: {
          message: fallbackSummary,
          sessionId,
          contextType,
          historyScope,
          leaderId: leaderId ?? (contextType === "leader" ? pageContext?.leaderId : null),
          relationshipRevision: contextType === "leader" ? pageContext?.relationshipRevision : null,
          captureClose: true,
          closePayload: {
            summary: fallbackSummary,
            actions: normalizedActions,
            leaderId: leaderId ?? (contextType === "leader" ? pageContext?.leaderId : null),
            transcriptUploadId: transcriptUploadId || null,
            coachCallId: coachCallId || null,
          },
          pageContext: pageContext ? {
            pageName: pageContext.pageName,
            leaderId: contextType === "leader" ? pageContext.leaderId : undefined,
            relationshipRevision: contextType === "leader" ? pageContext.relationshipRevision : undefined,
            data: pageContext.data,
          } : undefined,
        },
      });

      if (
        activeCloseRequestIdRef.current !== closeRequestId ||
        activeStorageScopeKeyRef.current !== closeScopeKey
      ) return null;

      if (fnError) {
        const errorCode = await extractSupabaseEdgeFunctionErrorCodeAsync(
          data,
          fnError,
        );
        if (
          activeCloseRequestIdRef.current !== closeRequestId ||
          activeStorageScopeKeyRef.current !== closeScopeKey
        ) return null;
        if (errorCode === "TRIAL_EXPIRED") {
          return {
            saved: false,
            error: "trial_expired",
            savedRoutes: [],
          };
        }
        if (errorCode === "COACH_SESSION_NOT_ATTESTED") {
          return {
            saved: false,
            error: "session_unattested",
            savedRoutes: [],
          };
        }
        throw fnError;
      }
      if (data?.error) throw new Error(data.error);

      if (data?.sessionId) {
        setSessionId(data.sessionId);
      }
      if (data?.conversationState) {
        setConversationState(data.conversationState as CoachConversationState);
      }

      // "empty_summary" means there was nothing to save, not a failure —
      // don't tell the coach to retry something that can't succeed.
      if (data?.closeCapture && data.closeCapture.saved === false && data.closeCapture.error !== "empty_summary") {
        setError("Close capture failed");
        toast.error("Couldn't save this conversation to coach memory. Please try again.");
      }
      const savedRoutes = data?.closeCapture?.savedRoutes || [];
      const systemMessage = data?.message
        || `Saved to Coach Memory${savedRoutes.length ? ` and routed to ${savedRoutes.join(", ")}.` : "."}`;

      if (!silent) {
        const assistantMessage: ChatMessage = {
          id: data?.messageId || crypto.randomUUID(),
          role: "assistant",
          content: systemMessage,
          createdAt: new Date(),
          coachInsight: data?.coachInsight ?? null,
        };
        setMessages((prev) => [...prev, assistantMessage]);
      }
      return data?.closeCapture ?? null;
    } catch (err: unknown) {
      if (
        activeCloseRequestIdRef.current !== closeRequestId ||
        activeStorageScopeKeyRef.current !== closeScopeKey
      ) return null;
      const message = err instanceof Error ? err.message : "Failed to close conversation";
      setError(message);
      toast.error("Couldn't save this conversation. Please try again.");
      return null;
    } finally {
      if (
        activeCloseRequestIdRef.current === closeRequestId &&
        activeStorageScopeKeyRef.current === closeScopeKey
      ) {
        activeCloseRequestIdRef.current = null;
        setIsLoading(false);
      }
    }
  }, [contextType, currentStorageScopeKey, historyScope, isLoading, isScopeReady, messages, pageContext, sessionId, user]);

  const captureCloseInBackground = useCallback(({
    actions,
    summary,
    transcriptUploadId,
    coachCallId,
  }: {
    actions: CoachCloseAction[];
    summary?: string;
    leaderId?: string | null;
    transcriptUploadId?: string | null;
    coachCallId?: string | null;
  }): boolean => {
    if (!user || contextType !== "coach") return false;
    if (messages.length === 0 && !streamingContentRef.current.trim()) return false;
    const authScope = authAccessTokenRef.current;
    if (!authScope || authScope.userId !== user.id) return false;
    const resolvedSessionId = sessionId || conversationId || null;
    if (!resolvedSessionId) return false;

    const latestMessage = [...messages].reverse().find((m) => m.content.trim().length > 0)?.content;
    const partialStreamingSummary = streamingContentRef.current.trim()
      ? `Partial response before close: ${streamingContentRef.current.trim()}`
      : null;
    const latestAssistant = [...messages].reverse().find((m) => m.role === "assistant")?.content;
    const fallbackSummary = truncateCloseCaptureSummary(
      summary?.trim()
        || partialStreamingSummary
        || latestAssistant
        || latestMessage
        || "Coach conversation captured.",
    );
    const normalizedActions = actions.includes("all")
      ? ["journal", "next_session", "leader_prep", "all"] as CoachCloseAction[]
      : actions;
    const resolvedTranscriptUploadId = transcriptUploadId || options?.transcriptUploadId || null;
    const resolvedCoachCallId = coachCallId || options?.coachCallId || null;

    const body = {
      message: fallbackSummary,
      sessionId: resolvedSessionId,
      contextType,
      historyScope,
      leaderId: null,
      transcriptUploadId: resolvedTranscriptUploadId,
      coachCallId: resolvedCoachCallId,
      captureClose: true,
      closePayload: {
        summary: fallbackSummary,
        actions: normalizedActions,
        leaderId: null,
        transcriptUploadId: resolvedTranscriptUploadId,
        coachCallId: resolvedCoachCallId,
      },
      pageContext: pageContext ? {
        pageName: pageContext.pageName,
      } : undefined,
    };

    void fetch(`${supabaseUrl}/functions/v1/coach-chat`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authScope.accessToken}`,
      },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => undefined);

    return true;
  }, [
    contextType,
    conversationId,
    historyScope,
    messages,
    options?.coachCallId,
    options?.transcriptUploadId,
    pageContext,
    sessionId,
    supabaseUrl,
    user,
  ]);

  const clearChat = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    activeRequestIdRef.current = null;
    activeCloseRequestIdRef.current = null;
    streamingContentRef.current = "";
    setMessages([]);
    setSessionId(null);
    setConversationState(null);
    setHarnessPayload(null);
    setIsLoading(false);
    setIsStreaming(false);
    setStreamingContent("");
    setError(null);
    removeEphemeralChatStorage(sessionStorageKey);
    removeEphemeralChatStorage(messagesStorageKey);
  }, [messagesStorageKey, sessionStorageKey]);

  return {
    messages: isScopeReady ? messages : [],
    conversationState: isScopeReady ? conversationState : null,
    harnessPayload: isScopeReady ? harnessPayload : null,
    isLoading: isLoading || !isScopeReady,
    isScopeReady,
    error,
    sendMessage,
    closeConversation,
    captureCloseInBackground,
    clearChat,
    messagesEndRef,
    streamingContent: isScopeReady ? streamingContent : "",
    isStreaming: isScopeReady ? isStreaming : false,
  };
}
